import { useDocumentTitle } from "usehooks-ts";
import Button from "../../../student/components/generic/button";
import {
  DataTableContextProvider,
  useDataTable,
} from "../DataTable/DataTableContext";
import { DataTable } from "../DataTable/DataTable";
import { EditSearchTagsModal } from "./EditSearchTagsModal";
import { SearchTagData } from "../../types";
import { useQueryClient } from "@tanstack/react-query";
import { DeleteSearchTagsModal } from "./DeleteSearchTagsModal";

const SEARCH_TAGS_QUERY_KEY = "/manager_new/module/search_tags/list";

export const EditSearchTagsPage = () => {
  useDocumentTitle("Edit Search Tags");
  const queryClient = useQueryClient();

  const reloadSearchTags = () => {
    queryClient.invalidateQueries({ queryKey: [SEARCH_TAGS_QUERY_KEY] });
  };

  return (
    <DataTableContextProvider
      source="/manager_new/module/search_tags/list"
      filterable={["searchTag", "associations"]}
      defaults={{ sortBy: [{ id: "searchTag", desc: false }] }}
      globalFilter
      fetchOnLoad
      queryKey={SEARCH_TAGS_QUERY_KEY}
      columnOverrides={{
        associations: ({ cell }: any) => (
          <span className="text-dm-charcoal-500">
            {cell?.value?.join(", ")}
          </span>
        ),
        edit: ({ row }) => (
          <EditSearchTagsModal
            key={"edit" + JSON.stringify((row as any).original)}
            onSave={reloadSearchTags}
            searchDataToEdit={(row as any).original as SearchTagData}
          >
            {(onClick) => (
              <Button type="link" onClick={onClick}>
                Edit
              </Button>
            )}
          </EditSearchTagsModal>
        ),
        delete: ({ row }) => (
          <DeleteSearchTagsModal
            key={"delete" + JSON.stringify((row as any).original)}
            onSave={reloadSearchTags}
            searchDataToDelete={(row as any).original as SearchTagData}
          >
            {(onClick) => (
              <Button
                type="link"
                className="text-red-500 hover:text-dm-error-600 active:text-dm-error-800"
                onClick={onClick}
              >
                Delete
              </Button>
            )}
          </DeleteSearchTagsModal>
        ),
      }}
    >
      <EditSearchTags onSave={reloadSearchTags} />
    </DataTableContextProvider>
  );
};

export const EditSearchTags = ({ onSave }: { onSave: () => void }) => {
  const { isFetching } = useDataTable();
  return (
    <div className="flex flex-col gap-6 p-6">
      <header>
        <h1 className="mb-2 text-2xl font-bold">Edit Search Tags</h1>
        <ul className="text-sm text-dm-charcoal-500">
          <li>
            <b>Search Tags:</b> These tags are used to search for skills
          </li>
          <li>
            <b>Associations:</b> These are additional tags that are associated
            with the search tag
          </li>
        </ul>
      </header>
      <EditSearchTagsModal onSave={onSave}>
        {(onClick) => (
          <Button
            className="flex w-auto items-center gap-3 !px-4"
            size="small"
            disabled={isFetching}
            onClick={onClick}
          >
            <i className="fas fa-plus" />
            Create new search tag
          </Button>
        )}
      </EditSearchTagsModal>
      <DataTable />
    </div>
  );
};
